// ---------------------------------------------------------------------------
// C82: Blog Detail Page — reading time, author card, TOC, related, share
// ---------------------------------------------------------------------------
import { useState, useEffect, useMemo, type ReactNode } from "react";
import { useParams, useSearchParams, Link } from "react-router";
import { formatDate } from "@/lib/i18n";
import {
  useBlogPost,
  useBlogComments,
  useBlogSeries,
  useBlogSeriesPosts,
  createBlogComment,
  likeComment,
  moderateComment,
  type BlogComment,
} from "@/hooks/use-blog";
import { Calendar, Clock, MessageSquare, Share2, ArrowLeft, ChevronUp, ThumbsUp, User, Loader2, Send, Pin, Trash2, CheckCircle, AlertTriangle } from "lucide-react";

interface PuckContentItem {
  type: string;
  props?: Record<string, unknown> & { id?: string };
}

function renderPuckContentItem(item: PuckContentItem, index: number, commentCount = 0) {
  const props = item.props ?? {};
  let rendered: ReactNode = null;

  switch (item.type) {
    case "Heading": {
      const level = String(props.level ?? "h2");
      const text = String(props.text ?? "");
      const align = String(props.align ?? "left");
      const className = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
      rendered = level === "h1"
        ? <h1 className={`text-4xl font-bold text-[var(--color-foreground)] ${className}`}>{text}</h1>
        : level === "h3"
          ? <h3 className={`text-2xl font-semibold text-[var(--color-foreground)] ${className}`}>{text}</h3>
          : <h2 className={`text-3xl font-semibold text-[var(--color-foreground)] ${className}`}>{text}</h2>;
      break;
    }
    case "Text":
    case "RichText": {
      const content = String(props.content ?? props.text ?? "");
      const align = String(props.align ?? "left");
      const className = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
      rendered = <p className={`whitespace-pre-wrap text-base leading-7 text-[var(--color-foreground)] ${className}`}>{content}</p>;
      break;
    }
    case "Image":
    case "ImageEnhanced": {
      const src = typeof props.src === "string" ? props.src : "";
      const alt = String(props.alt ?? props.caption ?? "Blog image");
      rendered = src
        ? <img src={src} alt={alt} className="w-full rounded-2xl object-cover" loading="lazy" />
        : null;
      break;
    }
    case "Button": {
      const label = String(props.label ?? "Learn more");
      const url = typeof props.url === "string" ? props.url : "#";
      rendered = (
        <div className="flex justify-center">
          <a
            href={url}
            className="inline-flex items-center justify-center rounded-xl bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {label}
          </a>
        </div>
      );
      break;
    }
    case "Hero": {
      const title = String(props.title ?? "");
      const subtitle = String(props.subtitle ?? "");
      const ctaLabel = String(props.ctaLabel ?? "");
      const ctaUrl = typeof props.ctaUrl === "string" ? props.ctaUrl : "#";
      rendered = (
        <section className="rounded-3xl bg-[var(--color-card)] px-6 py-10 text-center">
          <h2 className="text-4xl font-bold text-[var(--color-foreground)]">{title}</h2>
          {subtitle ? (
            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-[var(--color-muted-foreground)]">{subtitle}</p>
          ) : null}
          {ctaLabel ? (
            <div className="mt-6">
              <a
                href={ctaUrl}
                className="inline-flex items-center justify-center rounded-xl bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                {ctaLabel}
              </a>
            </div>
          ) : null}
        </section>
      );
      break;
    }
    case "Divider": {
      rendered = <hr className="border-[var(--color-border)]" />;
      break;
    }
    case "Blockquote": {
      const quote = String(props.quote ?? props.text ?? "");
      const author = String(props.author ?? "");
      rendered = (
        <blockquote className="rounded-2xl border-l-4 border-[var(--color-primary)] bg-[var(--color-card)] px-6 py-5">
          <p className="text-lg leading-8 text-[var(--color-foreground)]">“{quote}”</p>
          {author ? (
            <footer className="mt-3 text-sm text-[var(--color-muted-foreground)]">{author}</footer>
          ) : null}
        </blockquote>
      );
      break;
    }
    case "CodeBlock": {
      const code = String(props.code ?? "");
      const language = String(props.language ?? "text");
      rendered = (
        <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-slate-950">
          <div className="border-b border-white/10 px-4 py-2 text-xs uppercase tracking-[0.18em] text-slate-300">
            {language}
          </div>
          <pre className="overflow-x-auto px-4 py-4 text-sm leading-6 text-slate-100">
            <code>{code}</code>
          </pre>
        </div>
      );
      break;
    }
    case "MapEmbed": {
      const embedUrl = typeof props.embedUrl === "string"
        ? props.embedUrl
        : typeof props.url === "string"
          ? props.url
          : "";
      rendered = embedUrl ? (
        <div className="overflow-hidden rounded-2xl border border-[var(--color-border)]">
          <iframe
            src={embedUrl}
            title={String(props.title ?? "Embedded content")}
            className="h-[320px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : null;
      break;
    }
    default:
      rendered = null;
  }

  if (!rendered) {
    return null;
  }

  return (
    <div key={`${item.type}-${index}`} className="blog-content-block relative">
      {commentCount > 0 ? (
        <div className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)] shadow-sm">
          <MessageSquare className="h-3 w-3" />
          {commentCount}
        </div>
      ) : null}
      {rendered}
    </div>
  );
}

function TableOfContents({ content }: { content: unknown }) {
  const headings = useMemo(() => {
    if (!content || !Array.isArray(content)) return [];
    return (content as Array<{ type: string; props: Record<string, unknown> }>)
      .filter((c) => c.type === "Heading")
      .map((c) => ({
        text: String(c.props.text ?? ""),
        level: String(c.props.level ?? "h2"),
      }));
  }, [content]);

  if (headings.length < 2) return null;

  return (
    <nav className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <h4 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase mb-3">Table of Contents</h4>
      <ul className="space-y-1.5">
        {headings.map((h, i) => (
          <li key={i} className={`text-sm text-[var(--color-foreground)] hover:text-[var(--color-primary)] transition-colors ${h.level === "h3" ? "ml-4" : ""}`}>
            {h.text}
          </li>
        ))}
      </ul>
    </nav>
  );
}

function AuthorCard({ name, date }: { name: string; date: string | null }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-primary)]/10">
        <User className="h-5 w-5 text-[var(--color-primary)]" />
      </div>
      <div>
        <p className="text-sm font-medium text-[var(--color-foreground)]">{name}</p>
        {date && <p className="text-xs text-[var(--color-muted-foreground)]">{formatDate(date)}</p>}
      </div>
    </div>
  );
}

function ShareButtons({ title, slug }: { title: string; slug: string }) {
  const url = `${window.location.origin}/cms/blog/${slug}`;
  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[var(--color-muted-foreground)]">Share</span>
      <a href={`https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`} target="_blank" rel="noopener noreferrer" className="rounded-md p-1.5 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors">
        <Share2 className="h-3.5 w-3.5" />
      </a>
      <button
        onClick={() => { navigator.clipboard.writeText(url).catch(() => {}); }}
        className="rounded-md px-2 py-1 text-[10px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
      >
        Copy link
      </button>
    </div>
  );
}

function CommentThread({ comment, siteId, postId, onRefresh, depth = 0 }: { comment: BlogComment; siteId: string; postId: string; onRefresh: () => void; depth?: number }) {
  const [showReply, setShowReply] = useState(false);
  const [replyName, setReplyName] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModerating, setIsModerating] = useState(false);

  const handleReply = async () => {
    if (!replyContent.trim() || !replyName.trim()) return;
    setIsSubmitting(true);
    try {
      await createBlogComment(siteId, postId, { content: replyContent, authorName: replyName, parentId: comment.id });
      setReplyContent(""); setReplyName(""); setShowReply(false);
      onRefresh();
    } catch { /* error handled by API client */ }
    finally { setIsSubmitting(false); }
  };

  const handleModeration = async (action: "approve" | "spam" | "delete" | "pin") => {
    setIsModerating(true);
    try {
      await moderateComment(siteId, postId, comment.id, action);
      onRefresh();
    } catch {
      // API client surfaces the response to the operator.
    } finally {
      setIsModerating(false);
    }
  };

  const statusTone = comment.status === "approved"
    ? "bg-emerald-500/10 text-emerald-700"
    : comment.status === "spam"
      ? "bg-amber-500/10 text-amber-700"
      : comment.status === "deleted"
        ? "bg-slate-500/10 text-slate-700"
        : "bg-blue-500/10 text-blue-700";
  const aiTone = comment.aiClassification === "spam"
    ? "bg-rose-500/10 text-rose-700"
    : comment.aiClassification === "purchase_intent"
      ? "bg-emerald-500/10 text-emerald-700"
      : comment.aiClassification === "complaint"
        ? "bg-amber-500/10 text-amber-700"
        : "bg-violet-500/10 text-violet-700";
  const aiLabel = comment.aiClassification?.replace(/_/g, " ") ?? null;

  return (
    <div className={`${depth > 0 ? "ml-6 border-l-2 border-[var(--color-border)] pl-4" : ""}`}>
      <div className="rounded-lg bg-[var(--color-card)] p-3 mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[var(--color-foreground)]">{comment.authorName}</span>
            {comment.isPinned && <Pin className="h-2.5 w-2.5 text-[var(--color-primary)]" />}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] ${statusTone}`}>
              {comment.status}
            </span>
            {aiLabel ? (
              <span
                title={comment.aiRationale ?? undefined}
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] ${aiTone}`}
              >
                {aiLabel}
                {comment.aiConfidence != null ? ` ${Math.round(comment.aiConfidence * 100)}%` : ""}
              </span>
            ) : null}
            <span className="text-[10px] text-[var(--color-muted-foreground)]">{formatDate(comment.createdAt)}</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1">
            {comment.status === "approved" ? (
              <button onClick={() => likeComment(siteId, postId, comment.id).then(onRefresh)} className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)] hover:bg-[var(--color-muted)] transition-colors">
                <ThumbsUp className="h-2.5 w-2.5" /> {comment.likesCount > 0 ? comment.likesCount : ""}
              </button>
            ) : null}
            {comment.status !== "approved" ? (
              <button
                onClick={() => { void handleModeration("approve"); }}
                disabled={isModerating}
                className="rounded-md px-1.5 py-0.5 text-[10px] text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-50"
              >
                Approve
              </button>
            ) : null}
            <button
              onClick={() => { void handleModeration("pin"); }}
              disabled={isModerating}
              className="rounded-md px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] disabled:opacity-50"
            >
              {comment.isPinned ? "Unpin" : "Pin"}
            </button>
            {comment.status !== "spam" ? (
              <button
                onClick={() => { void handleModeration("spam"); }}
                disabled={isModerating}
                className="rounded-md px-1.5 py-0.5 text-[10px] text-amber-700 hover:bg-amber-500/10 disabled:opacity-50"
              >
                Spam
              </button>
            ) : null}
            {comment.status !== "deleted" ? (
              <button
                onClick={() => { void handleModeration("delete"); }}
                disabled={isModerating}
                className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-red-700 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 className="h-2.5 w-2.5" />
                Delete
              </button>
            ) : null}
            {isModerating ? (
              <Loader2 className="h-3 w-3 animate-spin text-[var(--color-muted-foreground)]" />
            ) : null}
          </div>
        </div>
        <p className="text-sm text-[var(--color-foreground)]">{comment.content}</p>
        {depth < 3 && (
          <button onClick={() => setShowReply(!showReply)} className="mt-1.5 text-[10px] text-[var(--color-primary)] hover:underline">
            Reply
          </button>
        )}
      </div>

      {showReply && (
        <div className="ml-6 mb-3 space-y-2">
          <input type="text" value={replyName} onChange={(e) => setReplyName(e.target.value)} placeholder="Your name" className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20" />
          <div className="flex gap-2">
            <input type="text" value={replyContent} onChange={(e) => setReplyContent(e.target.value)} placeholder="Write a reply..." className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20" />
            <button onClick={handleReply} disabled={isSubmitting || !replyContent.trim()} className="rounded-md p-1.5 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 disabled:opacity-30">
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {comment.replies?.map((reply) => (
        <CommentThread key={reply.id} comment={reply} siteId={siteId} postId={postId} onRefresh={onRefresh} depth={depth + 1} />
      ))}
    </div>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get("siteId") ?? "";
  const blogListPath = siteId ? `/cms/blog?siteId=${siteId}` : "/cms/blog";
  const { post, isLoading } = useBlogPost(siteId || undefined, slug);
  const {
    comments,
    blockCounts = {},
    isLoading: isLoadingComments,
    refetch: refetchComments,
  } = useBlogComments(siteId || undefined, post?.id, { includeBlockCounts: true });
  const { series } = useBlogSeries(siteId || undefined);
  const { posts: seriesPosts } = useBlogSeriesPosts(siteId || undefined, post?.seriesId);
  const [commentName, setCommentName] = useState("");
  const [commentContent, setCommentContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handler = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleComment = async () => {
    if (!post || !commentContent.trim() || !commentName.trim()) return;
    setIsSubmitting(true);
    try {
      await createBlogComment(siteId, post.id, { content: commentContent, authorName: commentName });
      setCommentContent(""); setCommentName("");
      refetchComments();
    } catch { /* error */ }
    finally { setIsSubmitting(false); }
  };

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" /></div>;
  }

  if (!post) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)]/30" />
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">Article not found.</p>
        <Link to={blogListPath} className="mt-3 inline-block text-sm text-[var(--color-primary)] hover:underline">Back to blog</Link>
      </div>
    );
  }

  const contentArray = (Array.isArray(post.content) ? post.content : []) as PuckContentItem[];
  const renderedContentBlocks = contentArray
    .map((item, index) => renderPuckContentItem(item, index, item.props?.id ? (blockCounts[item.props.id] ?? 0) : 0))
    .filter(Boolean);
  const currentSeries = post.seriesId ? (series.find((entry) => entry.id === post.seriesId) ?? null) : null;
  const orderedSeriesPosts = [...seriesPosts].sort((left, right) => (left.seriesOrder ?? Number.MAX_SAFE_INTEGER) - (right.seriesOrder ?? Number.MAX_SAFE_INTEGER));
  const currentSeriesIndex = orderedSeriesPosts.findIndex((entry) => entry.id === post.id);
  const previousSeriesPost = currentSeriesIndex > 0 ? orderedSeriesPosts[currentSeriesIndex - 1] : null;
  const nextSeriesPost = currentSeriesIndex >= 0 && currentSeriesIndex < orderedSeriesPosts.length - 1
    ? orderedSeriesPosts[currentSeriesIndex + 1]
    : null;
  const seriesPartLabel = post.seriesOrder && orderedSeriesPosts.length > 0
    ? `Part ${post.seriesOrder} of ${orderedSeriesPosts.length}`
    : null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back + meta */}
      <Link to={blogListPath} className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] mb-6">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to blog
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">
        {/* Main content */}
        <article>
          {/* Categories */}
          <div className="flex items-center gap-2 mb-3">
            {(post.categories ?? []).map((c) => (
              <Link
                key={c.id}
                to={`/cms/blog?siteId=${siteId}&categoryId=${c.id}`}
                className="px-2 py-0.5 text-xs font-medium rounded bg-[var(--color-primary)]/10 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20 transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>

          <h1 className="text-3xl font-bold text-[var(--color-foreground)] mb-4">{post.title}</h1>

          {currentSeries && seriesPartLabel ? (
            <div className="mb-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                {currentSeries.title}
              </p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{seriesPartLabel}</p>
              {(previousSeriesPost || nextSeriesPost) ? (
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  {previousSeriesPost ? (
                    <Link
                      to={`/cms/blog/${previousSeriesPost.slug}?siteId=${siteId}`}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      Previous: {previousSeriesPost.title}
                    </Link>
                  ) : null}
                  {nextSeriesPost ? (
                    <Link
                      to={`/cms/blog/${nextSeriesPost.slug}?siteId=${siteId}`}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      Next: {nextSeriesPost.title}
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Meta row */}
          <div className="flex items-center gap-4 text-sm text-[var(--color-muted-foreground)] mb-6">
            <span>{post.authorName}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{post.publishedAt ? formatDate(post.publishedAt) : "Draft"}</span>
            {post.readingTimeMin && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.readingTimeMin} min read</span>}
            <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{post.commentCount ?? 0} comments</span>
          </div>

          {/* Featured image */}
          {post.featuredImage && (
            <img src={post.featuredImage} alt={post.title} className="w-full rounded-xl mb-6 max-h-96 object-cover" loading="lazy" />
          )}

          <div className="prose prose-sm max-w-none text-[var(--color-foreground)]">
            {post.excerpt && <p className="text-lg text-[var(--color-muted-foreground)] mb-4">{post.excerpt}</p>}
            {renderedContentBlocks.length > 0 ? (
              <div className="space-y-6 not-prose">
                {renderedContentBlocks}
              </div>
            ) : (
              <p className="text-[var(--color-muted-foreground)]">
                This article body still depends on block types that only render on the published page path.
              </p>
            )}
          </div>

          {/* Share */}
          <div className="mt-8 pt-6 border-t border-[var(--color-border)]">
            <ShareButtons title={post.title} slug={post.slug} />
          </div>

          {(previousSeriesPost || nextSeriesPost) ? (
            <div className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                Continue the series
              </p>
              <div className="mt-3 flex flex-col gap-2 text-sm">
                {previousSeriesPost ? (
                  <Link
                    to={`/cms/blog/${previousSeriesPost.slug}?siteId=${siteId}`}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    Previous article: {previousSeriesPost.title}
                  </Link>
                ) : null}
                {nextSeriesPost ? (
                  <Link
                    to={`/cms/blog/${nextSeriesPost.slug}?siteId=${siteId}`}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    Next article: {nextSeriesPost.title}
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Comments */}
          {post.allowComments && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-4">
                Comments ({post.commentCount ?? 0})
              </h3>
              <p className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
                Comments are classified on the mounted blog service. High-confidence purchase-intent comments from verified authors emit CRM deal/task intelligence through the event-router target.
              </p>

              {/* New comment form */}
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 mb-6 space-y-3">
                <input type="text" value={commentName} onChange={(e) => setCommentName(e.target.value)} placeholder="Your name" className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20" />
                <textarea value={commentContent} onChange={(e) => setCommentContent(e.target.value)} placeholder="Share your thoughts..." rows={3} className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20" />
                <div className="flex justify-end">
                  <button onClick={handleComment} disabled={isSubmitting || !commentContent.trim() || !commentName.trim()} className="btn-press inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50" style={{ background: "var(--gradient-energy)" }}>
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post Comment
                  </button>
                </div>
              </div>

              {/* Comment threads */}
              {isLoadingComments ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" /></div>
              ) : comments.length === 0 ? (
                <p className="text-sm text-[var(--color-muted-foreground)] text-center py-8">No comments yet. Be the first.</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((c) => (
                    <CommentThread key={c.id} comment={c} siteId={siteId} postId={post.id} onRefresh={refetchComments} />
                  ))}
                </div>
              )}
            </div>
          )}
        </article>

        {/* Sidebar */}
        <aside className="space-y-4">
          <AuthorCard name={post.authorName} date={post.publishedAt} />
          <TableOfContents content={post.content} />
        </aside>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-20 right-6 rounded-full bg-[var(--color-card)] border border-[var(--color-border)] p-2.5 shadow-lg text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
